<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import ProfileDialog from '../../features/auth/ProfileDialog.vue';
import { useAuthStore } from '../../stores/auth';
import { useBonusShopStore } from '../../stores/bonusShop';

import UserAvatar from './UserAvatar.vue';

const auth = useAuthStore();
const bonusShop = useBonusShopStore();
const router = useRouter();

const open = ref(false);
const profileOpen = ref(false);
const root = ref<HTMLElement | null>(null);

function openProfile(): void {
  open.value = false;
  profileOpen.value = true;
}

onClickOutside(root, () => {
  open.value = false;
});

function toggle(): void {
  open.value = !open.value;
}

function close(): void {
  open.value = false;
}

function goto(name: string): void {
  close();
  void router.push({ name });
}

async function onLogout(): Promise<void> {
  close();
  await auth.logout();
  await router.push({ name: 'login' });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') close();
}
</script>

<template>
  <div
    ref="root"
    class="settings-menu account-menu"
    @keydown="onKeydown"
  >
    <button
      type="button"
      class="btn btn--ghost btn--small account-menu__trigger settings-menu__trigger"
      :aria-expanded="open"
      :aria-label="`Меню аккаунта${auth.user ? ' для ' + auth.user.email : ''}`"
      @click="toggle"
    >
      <UserAvatar
        v-if="auth.user"
        :first-name="auth.user.firstName"
        :last-name="auth.user.lastName"
        :avatar-url="auth.user.avatarUrl"
        size="sm"
      />
      <span
        class="account-menu__email"
        :title="auth.user?.email"
      >
        {{ auth.user?.email ?? '…' }}
      </span>
      <span
        class="account-menu__caret"
        aria-hidden="true"
      >▾</span>
    </button>

    <div
      v-show="open"
      class="settings-menu__panel account-menu__panel"
      role="menu"
    >
      <p
        v-if="auth.user?.email"
        class="account-menu__panel-email"
      >
        {{ auth.user.email }}
      </p>
      <div
        v-if="auth.user?.email"
        class="account-menu__divider"
      ></div>
      <button
        v-if="auth.user?.isAdmin"
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="goto('adminland')"
      >
        Админка
      </button>
      <button
        v-if="auth.user?.isAdmin"
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="goto('management-dashboard')"
      >
        Management
      </button>
      <button
        v-if="auth.user?.isSuperadmin"
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="goto('platform')"
      >
        Superadmin
      </button>
      <div
        v-if="auth.user?.isAdmin"
        class="account-menu__divider"
      ></div>
      <button
        v-if="auth.user?.membershipType === 'employee' && bonusShop.gamificationEnabled && bonusShop.programEnabled"
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="goto('rewards')"
      >
        Бонусы
      </button>
      <button
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="goto('booking-availability')"
      >
        Доступность для звонков
      </button>
      <button
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="goto('archive')"
      >
        Архив
      </button>
      <button
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="openProfile"
      >
        Редактировать профиль
      </button>
      <div class="account-menu__divider"></div>
      <button
        type="button"
        class="settings-menu__item"
        role="menuitem"
        @click="onLogout"
      >
        Выйти
      </button>
    </div>
    <ProfileDialog
      :open="profileOpen"
      @close="profileOpen = false"
    />
  </div>
</template>
